import { type EdgeProps, type Edge as FlowEdge, useInternalNode } from "@xyflow/react";
import React from "react";

import { NODE_HEIGHT, NODE_WIDTH } from "./treeGraph.constants";
import type { RingGuide } from "./treeGraph.types";

import styles from "./RingGuideEdge.module.css";

type RingGuideEdgeData = { guides: RingGuide[] };

/**
 * Decorative concentric circles for the radial layout, centered on the focus entity with a small
 * junction dot where each spoke crosses its circle. Rendered as a self-edge on the center node so
 * it lives in the edge layer (beneath every card) and follows the center if it is dragged.
 */
const RingGuideEdge = ({ data, source }: EdgeProps<FlowEdge<RingGuideEdgeData>>) => {
	const centerNode = useInternalNode(source);

	if (!centerNode || !data) {
		return null;
	}

	const cx = centerNode.internals.positionAbsolute.x + (centerNode.measured.width ?? NODE_WIDTH) / 2;
	const cy = centerNode.internals.positionAbsolute.y + (centerNode.measured.height ?? NODE_HEIGHT) / 2;

	return (
		<g className={styles.ringGuides}>
			{data.guides.map((guide: RingGuide) => (
				<g key={guide.radius}>
					<circle className={styles.ring} cx={cx} cy={cy} r={guide.radius} />
					{guide.dotAngles.map((angle: number) => (
						<circle
							key={angle}
							className={styles.junctionDot}
							cx={cx + guide.radius * Math.cos(angle)}
							cy={cy + guide.radius * Math.sin(angle)}
							r={5}
						/>
					))}
				</g>
			))}
		</g>
	);
};

export default RingGuideEdge;
